<template>
	<Teleport to="body">
		<!-- Обертка для анимации появления/исчезновения -->
		<Transition name="modal-fade">
			<div v-if="isVisible" class="blocked-modal-overlay">
				
				<div class="blocked-modal">
					<!-- Декоративная полоса опасности -->
					<div class="blocked-modal__status-bar"></div>

					<div class="blocked-modal__content">
						<div class="blocked-modal__header">
							<div class="blocked-modal__icon-wrapper">
								<svg
									width="48"
									height="48"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									stroke-width="1.5"
									stroke-linecap="round"
									stroke-linejoin="round"
								>
									<circle cx="12" cy="12" r="10" />
									<line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
								</svg>
							</div>
							<h3 class="blocked-modal__title">Аккаунт заблокирован</h3>
						</div>

						<div class="blocked-modal__body">
							<p class="blocked-modal__message">
								{{ message || 'Ваш аккаунт заблокирован администратором.' }}
							</p>
							<p class="blocked-modal__hint">
								Доступ к системе временно ограничен.<br>
								Для разблокировки обратитесь к администратору.
							</p>
						</div>

						<div class="blocked-modal__footer">
							<button
								type="button"
								class="blocked-modal__btn"
								@click="logout"
							>
								Выйти из системы
							</button>
						</div>
					</div>
				</div>

			</div>
		</Transition>
	</Teleport>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const isVisible = ref(false)
const message = ref('')

const handleBlocked = (event) => {
	message.value = event.detail?.message || ''
	isVisible.value = true
}

const logout = () => {
	isVisible.value = false
	message.value = ''
	window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason: 'account_blocked' } }))
}

onMounted(() => {
	window.addEventListener('auth:blocked', handleBlocked)
})

onUnmounted(() => {
	window.removeEventListener('auth:blocked', handleBlocked)
})
</script>

<style scoped>
/* Подключение шрифтов (если они не подключены глобально) */
@import url('https://fonts.googleapis.com/css2?family=Bitter:wght@400;500;600&family=Montserrat:wght@300;400;500&display=swap');

/* Переменные цветов */
:root {
	--corp-white: #ffffff;
	--corp-blue: #0096ed;
	--corp-grey: #636363;
	--corp-danger: #e53935; /* Красный для опасности */
}

.blocked-modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(99, 99, 99, 0.4); /* Серый полупрозрачный фон */
	backdrop-filter: blur(6px);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 10002;
}

.blocked-modal {
	background: #ffffff;
	width: 100%;
	max-width: 480px;
	border-radius: 8px; /* Более строгие углы */
	box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
	overflow: hidden;
	position: relative;
	/* Начальное состояние анимации задается через Transition классы ниже */
}

/* Красная полоса сверху */
.blocked-modal__status-bar {
	height: 4px;
	width: 100%;
	background-color: #e53935;
}

.blocked-modal__content {
	padding: 40px 32px 32px;
}

.blocked-modal__header {
	text-align: center;
	margin-bottom: 24px;
}

.blocked-modal__icon-wrapper {
	color: #e53935;
	margin-bottom: 16px;
	display: inline-flex;
	padding: 12px;
	background: rgba(229, 57, 53, 0.08); /* Легкий красный фон под иконкой */
	border-radius: 50%;
}

.blocked-modal__title {
	margin: 0;
	font-family: 'Bitter', serif; /* Корпоративный шрифт заголовков */
	font-weight: 500;
	font-size: 24px;
	color: #2c2c2c; /* Чуть темнее основного серого для контраста заголовка */
}

.blocked-modal__body {
	text-align: center;
	margin-bottom: 32px;
}

.blocked-modal__message {
	margin: 0 0 16px;
	font-family: 'Montserrat', sans-serif;
	font-weight: 400; /* Чуть плотнее лайта для читаемости важного сообщения */
	font-size: 16px;
	color: #636363;
	line-height: 1.5;
}

.blocked-modal__hint {
	margin: 0;
	font-family: 'Montserrat', sans-serif;
	font-weight: 300; /* Light, как требовалось */
	font-size: 14px;
	color: #8d8d8d;
	line-height: 1.6;
}

.blocked-modal__footer {
	display: flex;
	justify-content: center;
}

.blocked-modal__btn {
	font-family: 'Montserrat', sans-serif;
	font-weight: 500;
	font-size: 14px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	
	background-color: #0096ed; /* Корпоративный синий */
	color: #ffffff;
	
	padding: 14px 40px;
	border: none;
	border-radius: 4px; /* Строгие скругления */
	cursor: pointer;
	
	transition: background-color 0.2s ease, transform 0.1s ease;
	box-shadow: 0 4px 12px rgba(0, 150, 237, 0.3);
}

.blocked-modal__btn:hover {
	background-color: #007ac2; /* Затемнение при наведении */
	transform: translateY(-1px);
}

.blocked-modal__btn:active {
	transform: translateY(0);
}

/* Анимации Vue Transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
	transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
	opacity: 0;
}

/* Анимация самого модального окна внутри обертки */
.modal-fade-enter-active .blocked-modal {
	animation: modal-slide-in 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.modal-fade-leave-active .blocked-modal {
	animation: modal-slide-in 0.3s cubic-bezier(0.16, 1, 0.3, 1) reverse;
}

@keyframes modal-slide-in {
	from {
		opacity: 0;
		transform: translateY(20px) scale(0.98);
	}
	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
}

@media (prefers-reduced-motion: reduce) {
	.modal-fade-enter-active,
	.modal-fade-leave-active,
	.modal-fade-enter-active .blocked-modal,
	.modal-fade-leave-active .blocked-modal {
		animation: none;
		transition: none;
	}
}
</style>